'use no memo';

/**
 *
 * DrawerMenu
 *
 */

import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { Link } from '@tanstack/react-router';
import { useProjectName } from '~/hooks/api/useProjectName';
import { useFeatureFlag } from '~/hooks/api/useFeatureFlags';

import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import MaterialLink from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';

import DepotIcon from '@mui/icons-material/AccountBalance';
import ProfileIcon from '@mui/icons-material/AccountCircle';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TurnReportIcon from '@mui/icons-material/Assignment';
import ExperiencesIcon from '@mui/icons-material/Chat';
import NewsIcon from '@mui/icons-material/Comment';
import ContactIcon from '@mui/icons-material/ContactSupport';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TermsIcon from '@mui/icons-material/Description';
import CircuitIcon from '@mui/icons-material/DonutLarge';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LogoutIcon from '@mui/icons-material/ExitToApp';
import UsersIcon from '@mui/icons-material/Group';
import DocumentsIcon from '@mui/icons-material/InsertDriveFile';
import CongregationIcon from '@mui/icons-material/LocationCity';
import PrivacyIcon from '@mui/icons-material/Lock';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import CalendarIcon from '@mui/icons-material/PermContactCalendar';
import SettingsIcon from '@mui/icons-material/Settings';
import RouteIcon from '@mui/icons-material/TransferWithinAStation';
import { ListItemButton } from '@mui/material';

import ExpandableListItem from '~/components/ExpandableListItem';
import CurrentAppVersion from '~/containers/CurrentAppVersion';
import HasRole from '~/containers/HasRole';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import Spacings from '~/modules/ui-kit/spacings';
import { useDrawerMenu } from './DrawerMenuContext';
import messages from './messages';
import styles from './styles.module.css';
import { useMatchRoute, useNavigate } from '@tanstack/react-router';

const EmergencyModeIcon = styled(NewReleasesIcon)(({ theme }) => ({
  color: theme.palette.error.main,
}));

const EmergencyModeText = styled(ListItemText)(({ theme }) => ({
  '& .MuiListItemText-primary': {
    color: theme.palette.error.main,
    fontWeight: 500,
  },
}));

export const publicItems = (isExperiencesFeatureEnabled: boolean) =>
  [
    { route: '/home', label: messages.dashboard, icon: DashboardIcon },
    { route: '/application', label: messages.calendar, icon: CalendarIcon },
    {
      route: '/availability',
      label: messages.availability,
      icon: EventAvailableIcon,
    },
    isExperiencesFeatureEnabled
      ? {
          route: '/experiences',
          label: messages.experiences,
          icon: ExperiencesIcon,
        }
      : undefined,
    { route: '/user-profile', label: messages.userProfile, icon: ProfileIcon },
    { route: '/documents', label: messages.documents, icon: DocumentsIcon },
  ].filter((item) => item !== undefined);

export const administrationItems = (
  isExperiencesFeatureEnabled: boolean,
  isEmergencyModeEnabled: boolean
) =>
  [
    { route: '/weekplan', label: messages.weekplan, icon: CalendarIcon },
    {
      route: '/turn-reports',
      label: messages.turnReports,
      icon: TurnReportIcon,
    },
    { route: '/trolley-routes', label: messages.routeList, icon: RouteIcon },
    { route: '/depots', label: messages.depotManagement, icon: DepotIcon },
    {
      route: '/circuits',
      label: messages.circuitManagement,
      icon: CircuitIcon,
    },
    {
      route: '/congregations',
      label: messages.congregationManagement,
      icon: CongregationIcon,
    },
    { route: '/users', label: messages.userManagement, icon: UsersIcon },
    {
      route: '/news-entries',
      label: messages.newsEntryManagement,
      icon: NewsIcon,
    },
    { route: '/reports/branch', label: messages.reports, icon: AssessmentIcon },

    isExperiencesFeatureEnabled
      ? {
          route: '/experienceList',
          label: messages.experiences,
          icon: ExperiencesIcon,
        }
      : undefined,
    {
      label: messages.settings,
      icon: SettingsIcon,
      subItems: [
        {
          route: '/settings/planning',
          label: messages.planningSettings,
        },
        {
          route: '/settings/planningSchedule',
          label: messages.planningScheduleSettings,
        },
        {
          route: '/settings/news',
          label: messages.newsSettings,
        },
        {
          route: '/settings/reporting',
          label: messages.reportingSettings,
        },
        {
          route: '/settings/project-settings',
          label: messages.projectSettings,
        },
        {
          route: '/settings/participation',
          label: messages.participationSettings,
        },
        {
          route: '/settings/smtp',
          label: messages.smtpSettings,
        },
        {
          route: '/settings/masterData',
          label: messages.masterdataSettings,
        },
      ],
    },
    isEmergencyModeEnabled
      ? {
          route: '/emergency-mode',
          label: messages.emergencyMode,
          icon: EmergencyModeIcon,
          customText: EmergencyModeText,
        }
      : undefined,
  ].filter((item) => item !== undefined);

export const legalItems = [
  {
    key: 'contact',
    route: '/contact',
    label: messages.contact,
    icon: ContactIcon,
  },
  { key: 'terms', route: '/terms', label: messages.terms, icon: TermsIcon },
  {
    key: 'privacyPolicy',
    route: '/privacy',
    label: messages.privacyPolicy,
    icon: PrivacyIcon,
  },
];

type DrawerMenuProps = {
  variant?: 'permanent' | 'temporary';
  forceOpen?: boolean;
};

export const DrawerMenu = (props: DrawerMenuProps) => {
  const {
    drawerMenu: { isOpen },
    hideDrawerMenu,
  } = useDrawerMenu();
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const { data: reportingSettings } = useSettingsByCategory('reporting');
  const { isFetching: isFetchingProjectName, data: projectName } =
    useProjectName();
  const { data: isEmergencyModeEnabled } = useFeatureFlag('EmergencyMode');

  const navigateToRoute = (route: string) => {
    hideDrawerMenu();
    navigate({ to: route });
  };
  const isExperiencesFeatureEnabled =
    (reportingSettings?.isExperiencesFeatureEnabled as boolean) ?? false;
  return (
    <Drawer
      data-test="drawer-button"
      open={props.forceOpen || isOpen}
      onClose={() => hideDrawerMenu()}
      variant={props.variant}
    >
      <Spacings.Inset>
        <MaterialLink
          variant="h6"
          color="textSecondary"
          component={Link}
          to="/"
          underline="none"
        >
          {!isFetchingProjectName ? projectName : ''}
        </MaterialLink>
      </Spacings.Inset>
      <Divider />
      <List style={{ width: 300, overflowY: 'auto' }}>
        {publicItems(isExperiencesFeatureEnabled).map((item, index) => (
          <ListItemButton
            data-test-type="drawer-menu-button"
            key={index}
            onClick={() => navigateToRoute(item.route)}
            selected={Boolean(
              matchRoute({
                to: item.route,
              })
            )}
          >
            <ListItemIcon>
              <item.icon />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage {...item.label} />} />
          </ListItemButton>
        ))}
        <HasRole roles={['Administrator']}>
          <Divider />
          <ListSubheader disableSticky>
            <FormattedMessage {...messages.administration} />
          </ListSubheader>
          {administrationItems(
            isExperiencesFeatureEnabled,
            isEmergencyModeEnabled ?? false
          ).map((item) =>
            item.subItems ? (
              <ExpandableListItem
                data-test-type="drawer-menu-button"
                key={item.label.id}
                icon={<item.icon />}
                primaryLabel={<FormattedMessage {...item.label} />}
                open={false}
              >
                {item.subItems.map((subItem) => (
                  <ListItemButton
                    data-test-type="drawer-menu-button"
                    key={subItem.label.id}
                    onClick={() => navigateToRoute(subItem.route)}
                    selected={Boolean(
                      matchRoute({
                        to: subItem.route,
                      })
                    )}
                  >
                    <ListItemText
                      primary={<FormattedMessage {...subItem.label} />}
                    />
                  </ListItemButton>
                ))}
              </ExpandableListItem>
            ) : (
              <ListItemButton
                data-test-type="drawer-menu-button"
                key={item.label.id}
                onClick={() => navigateToRoute(item.route)}
                selected={Boolean(
                  matchRoute({
                    to: item.route,
                  })
                )}
              >
                <ListItemIcon>
                  <item.icon />
                </ListItemIcon>
                {item.customText ? (
                  <item.customText
                    primary={<FormattedMessage {...item.label} />}
                  />
                ) : (
                  <ListItemText
                    primary={<FormattedMessage {...item.label} />}
                  />
                )}
              </ListItemButton>
            )
          )}
        </HasRole>
        <Divider />
        {legalItems.map((item) => (
          <ListItemButton
            data-test={item.key}
            data-test-type="drawer-menu-button"
            key={item.label.id}
            onClick={() => navigateToRoute(item.route)}
          >
            <ListItemIcon>
              <item.icon />
            </ListItemIcon>
            <ListItemText primary={<FormattedMessage {...item.label} />} />
          </ListItemButton>
        ))}
        <ListItem
          href="/Identity/Account/Logout"
          data-test="logout-button"
          data-test-type="drawer-menu-button"
          component="a"
        >
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary={<FormattedMessage {...messages.logout} />} />
        </ListItem>
      </List>
      <div className={styles['current-app-version']}>
        <FormattedMessage
          {...messages.appVersion}
          values={{ version: <CurrentAppVersion /> }}
        />
      </div>
    </Drawer>
  );
};

export default DrawerMenu;
